import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import type { GoogleSignInResult } from "./auth";

type IdentityApi = {
  initialize: (options: { client_id: string; nonce: string; callback: (response: { credential: string }) => void; auto_select: boolean; ux_mode: "popup" }) => void;
  renderButton: (element: HTMLElement, options: { type: "standard"; theme: "outline"; size: "large"; text: "signup_with" | "signin_with"; shape: "pill"; width: number; locale: "en" }) => void;
};
type GoogleWindow = Window & { google?: { accounts: { id: IdentityApi } } };
let sdkPromise: Promise<IdentityApi> | undefined;
function loadGoogle(): Promise<IdentityApi> {
  const existing = (window as GoogleWindow).google?.accounts.id;
  if (existing) return Promise.resolve(existing);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<IdentityApi>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.referrerPolicy = "strict-origin-when-cross-origin";
    const timeout = window.setTimeout(() => fail(), 10_000);
    const fail = () => { window.clearTimeout(timeout); script.remove(); sdkPromise = undefined; reject(new Error("Google could not load. Try again or continue with email.")); };
    script.onerror = fail;
    script.onload = () => {
      window.clearTimeout(timeout);
      const api = (window as GoogleWindow).google?.accounts.id;
      if (api) resolve(api); else fail();
    };
    document.head.append(script);
  });
  return sdkPromise;
}

const callbackErrors = new Map([
  ["cancelled", "Google sign-in was cancelled. Try again or continue with email."],
  ["failed", "Google sign-in could not be verified. Please try again."],
  ["account_conflict", "This email already has an account. Try Google again to confirm your existing password and connect it."],
]);

export function GoogleSignIn({ signup, disabled, signIn, onSuccess, onBusyChange, destination = "/ai" }: {
  signup: boolean; disabled: boolean; destination?: string;
  signIn: (credential: string, password?: string) => Promise<GoogleSignInResult>;
  onSuccess: () => void; onBusyChange: (busy: boolean) => void;
}) {
  const location = useLocation();
  const callbackCode = new URLSearchParams(location.search).get("google") ?? "";
  // Existing accounts still use the password-confirmed popup linking flow.
  const preferPopup = callbackCode === "account_conflict";
  const container = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLParagraphElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const handlers = useRef({ signIn, onSuccess, onBusyChange, disabled });
  useEffect(() => { handlers.current = { signIn, onSuccess, onBusyChange, disabled }; }, [signIn, onSuccess, onBusyChange, disabled]);
  const pending = useRef(false);
  const alive = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState(false);
  const [redirectMode, setRedirectMode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() => callbackErrors.get(callbackCode) ?? null);
  const [linkCredential, setLinkCredential] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { if (error) alertRef.current?.focus(); }, [error]);
  useEffect(() => { if (linkCredential) passwordRef.current?.focus(); }, [linkCredential]);
  useEffect(() => {
    const restore = (event: PageTransitionEvent) => {
      if (event.persisted) { pending.current = false; setBusy(false); handlers.current.onBusyChange(false); }
    };
    window.addEventListener("pageshow", restore);
    return () => window.removeEventListener("pageshow", restore);
  }, []);

  async function startRedirect() {
    if (pending.current || handlers.current.disabled) return;
    pending.current = true; setBusy(true); setError(null); handlers.current.onBusyChange(true);
    try {
      const response = await fetch("/api/v1/auth/google/start", {
        method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000),
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ destination }),
      });
      if (!response.ok) throw new Error(response.status === 429 ? "Too many attempts. Please wait 15 minutes before trying again." : "Google sign-in is temporarily unavailable. Please try again.");
      const body = await response.json() as { url: string };
      const url = new URL(body.url);
      if (url.origin !== "https://accounts.google.com" || url.pathname !== "/o/oauth2/v2/auth" || url.username || url.password) throw new Error("Google sign-in could not be started. Please try again.");
      window.location.assign(url.href);
    } catch (failure) {
      if (alive.current) {
        setError(failure instanceof Error && /^(Google sign-in|Too many attempts)/.test(failure.message) ? failure.message : "Google sign-in could not be started. Check your connection and try again.");
        setBusy(false); handlers.current.onBusyChange(false);
      }
      pending.current = false;
    }
  }

  async function authenticate(credential: string, accountPassword?: string) {
    if (pending.current || handlers.current.disabled) return;
    pending.current = true; setBusy(true); setError(null); handlers.current.onBusyChange(true);
    try {
      const result = await handlers.current.signIn(credential, accountPassword);
      if (!alive.current) return;
      if (!result) { handlers.current.onSuccess(); return; }
      if (result.code === "google_link_required") setLinkCredential(credential);
      else {
        if (result.code === "invalid_google_credential") { setLinkCredential(null); setPassword(""); }
        setError(result.message);
      }
    } catch { if (alive.current) setError("We couldn’t reach the account service. Try again or continue with email."); }
    finally {
      pending.current = false;
      if (alive.current) { setBusy(false); handlers.current.onBusyChange(false); }
    }
  }
  const authenticateRef = useRef(authenticate);
  useEffect(() => { authenticateRef.current = authenticate; });

  useEffect(() => {
    let active = true;
    let observer: ResizeObserver | undefined;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    void (async () => {
      try {
        const response = await fetch(`/api/v1/auth/google/config${preferPopup ? "?mode=popup" : ""}`, { credentials: "same-origin", signal: controller.signal });
        const config = response.ok && response.headers.get("content-type")?.includes("application/json")
          ? await response.json() as { clientId?: string; nonce?: string; redirect?: boolean } : null;
        if (!active) return;
        if (config?.redirect === true && !preferPopup) { setRedirectMode(true); return; }
        if (!config?.clientId || !config.nonce) {
          if (attempt > 0) setError("Google sign-in is currently unavailable. Please continue with email.");
          return;
        }
        const api = await loadGoogle();
        if (!active || !container.current) return;
        api.initialize({ client_id: config.clientId, nonce: config.nonce, auto_select: false, ux_mode: "popup", callback: response => {
          if (active && response.credential) void authenticateRef.current(response.credential);
        } });
        const element = container.current;
        let previousWidth = 0;
        const render = () => {
          const width = Math.floor(Math.min(element.getBoundingClientRect().width, 400));
          if (width < 1 || width === previousWidth) return;
          previousWidth = width;
          element.replaceChildren();
          api.renderButton(element, { type: "standard", theme: "outline", size: "large", text: signup ? "signup_with" : "signin_with", shape: "pill", width, locale: "en" });
        };
        render();
        observer = new ResizeObserver(render); observer.observe(element);
        setReady(true);
      } catch {
        if (active) setError("Google could not load. Try again or continue with email.");
      } finally { window.clearTimeout(timeout); }
    })();
    return () => { active = false; controller.abort(); window.clearTimeout(timeout); observer?.disconnect(); };
  }, [attempt, signup, preferPopup]);

  function connect(event: FormEvent) { event.preventDefault(); if (linkCredential && password) void authenticate(linkCredential, password); }
  function retry() { container.current?.replaceChildren(); setError(null); setReady(false); setAttempt(value => value + 1); }
  return <div className="auth-google" aria-busy={busy}>
    <div className="auth-google__button" ref={container} inert={disabled || busy || Boolean(linkCredential)} hidden={Boolean(linkCredential)} />
    {!ready && !linkCredential ? <button className="auth-google__fallback" type="button" disabled={disabled || busy} onClick={redirectMode ? () => void startRedirect() : retry} aria-label="Sign in with Google">
      <img src={`${import.meta.env.BASE_URL}auth/google-signin-light.png`} alt="" width="180" height="40" />
    </button> : null}
    {busy ? <p className="auth-google__status" role="status">{redirectMode ? "Opening Google…" : "Connecting your Google account…"}</p> : null}
    {linkCredential ? <form className="auth-google__link auth-form" onSubmit={connect} aria-label="Connect Google to your account">
      <p>This email already has an account. Confirm your password once to connect Google and keep your stencils and plan.</p>
      <div className="auth-field"><label htmlFor="google-account-password">Existing account password</label><input ref={passwordRef} id="google-account-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required readOnly={busy} /></div>
      <button className="auth-submit" type="submit" disabled={busy || !password}>Connect Google and sign in</button>
      <div className="auth-google__link-actions"><button type="button" disabled={busy} onClick={() => { setLinkCredential(null); setPassword(""); setError(null); }}>Cancel</button><Link to="/forgot-password">Forgot your password?</Link></div>
    </form> : null}
    {error ? <p className="auth-error" role="alert" tabIndex={-1} ref={alertRef}>{error}{!linkCredential ? <button className="auth-google__retry" type="button" disabled={busy} onClick={redirectMode ? () => void startRedirect() : retry}>Try Google again</button> : null}</p> : null}
    <div className="auth-divider"><span>or</span></div>
  </div>;
}
